import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, Star, Trash2, Lock, ArrowRight, Loader2, BadgeCheck, LogOut } from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import { api, type FavoriteRow, type MyListingRow, type MyWTBRow, type MeProfile } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth, shortAddr } from "@/lib/auth";
import { StateBadge } from "@/components/StateBadge";

export function Me() {
  const { t, fmtDate } = useI18n();
  const { wallet, walletChecksum, loading: authLoading, connect, logout } = useAuth();
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [favs, setFavs] = useState<FavoriteRow[]>([]);
  const [listings, setListings] = useState<MyListingRow[]>([]);
  const [wtbs, setWtbs] = useState<MyWTBRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wallet) return;
    setLoading(true);
    setErr(null);
    try {
      const [me, f, l, w] = await Promise.all([
        api<MeProfile>("/api/auth/me"),
        api<{ items: FavoriteRow[] }>("/api/me/favorites"),
        api<{ items: MyListingRow[] }>("/api/me/listings"),
        api<{ items: MyWTBRow[] }>("/api/me/wtb"),
      ]);
      setProfile(me);
      setFavs(f.items || []);
      setListings(l.items || []);
      setWtbs(w.items || []);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    load();
  }, [load]);

  const onRemoveFav = async (cardId: string) => {
    if (!confirm(t("me.confirm_remove_fav") || `Remove ${cardId} from favorites?`)) return;
    try {
      await api(`/api/me/favorites/${encodeURIComponent(cardId)}`, { method: "DELETE" });
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onCancelListing = async (id: number) => {
    if (!confirm(t("me.confirm_cancel_listing") || `Cancel listing #${id}?`)) return;
    try {
      await api(`/api/listings/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onCancelWtb = async (id: number) => {
    if (!confirm(t("me.confirm_cancel_wtb") || `Cancel WTB #${id}?`)) return;
    try {
      await api(`/api/wtb/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  if (authLoading) {
    return (
      <div className="grid place-items-center py-16 text-muted-fg">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!wallet) {
    return (
      <Card className="mx-auto max-w-md">
        <CardBody className="grid place-items-center gap-3 p-8 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-warn/15 text-warn">
            <Lock className="h-5 w-5" />
          </div>
          <CardTitle>{t("me.login_required.title") || "Connect to view your profile"}</CardTitle>
          <CardDescription>
            {t("me.login_hint") || "You are not signed in. Click Connect to sign in with MetaMask."}
          </CardDescription>
          <Button onClick={() => connect().catch((e) => alert(e.message))}>
            <Lock className="h-3.5 w-3.5" /> Connect wallet
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
          <User className="h-3.5 w-3.5" />
          {t("me.kicker") || "My profile"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("me.title") || "My profile"}
        </h1>
      </header>

      {err ? (
        <Card>
          <CardBody>
            <Empty title={t("common.error") || "Error"} description={err} />
          </CardBody>
        </Card>
      ) : null}

      {/* Identity */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("me.identity") || "Identity"}</CardTitle>
            <CardDescription>{t("me.identity.help") || "SIWE-authenticated wallet."}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone="success" variant="soft">
              <BadgeCheck className="h-3 w-3" /> Signed
            </Badge>
            {/* PO-28 : bouton « Se déconnecter » sur /me. */}
            <Button size="sm" variant="secondary" data-testid="me-logout" onClick={() => logout().catch((e) => alert(e.message))}>
              <LogOut className="h-3.5 w-3.5" /> {t("me.logout") || "Se déconnecter"}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="grid gap-3 sm:grid-cols-3">
          <Field label={t("me.address") || "Address"}>
            <span className="font-mono text-xs" title={walletChecksum ?? wallet ?? ""}>
              {shortAddr(walletChecksum ?? wallet)}
            </span>
          </Field>
          <Field label={t("me.created_at") || "Created"}>
            <span className="text-xs">{fmtDate(profile?.profile?.created_at)}</span>
          </Field>
          <Field label={t("me.last_login") || "Last login"}>
            <span className="text-xs">{fmtDate(profile?.profile?.last_login_at)}</span>
          </Field>
        </CardBody>
      </Card>

      {/* Listings */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("me.listings") || "My listings"}</CardTitle>
            <CardDescription>{listings.length} active or historical.</CardDescription>
          </div>
          <Link
            to="/sell"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-fg shadow-soft hover:bg-primary/90"
          >
            {t("nav.sell_card") || "Sell a card"} <ArrowRight className="h-3 w-3" />
          </Link>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH>Card</TH>
                  <TH>État</TH>
                  <TH>Cert</TH>
                  <TH className="text-right">Price (USDC)</TH>
                  <TH>Status</TH>
                  <TH>Published</TH>
                  <TH className="text-right">Action</TH>
                </TR>
              </THead>
              <TBody>
                {loading && listings.length === 0 ? (
                  <tr><td colSpan={7}><div className="grid place-items-center py-6 text-muted-fg"><Loader2 className="h-4 w-4 animate-spin" /></div></td></tr>
                ) : listings.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <Empty
                        title={t("me.no_listings") || "No listings yet"}
                        description={t("me.no_listings.desc") || "Use Sell a card to publish your first slab."}
                        action={
                          <Link to="/sell" className="text-xs text-primary hover:underline">
                            {t("me.sell_card_link") || "List a card →"}
                          </Link>
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  listings.map((l) => (
                    <TR key={l.id}>
                      <TD>
                        <Link to={`/card/${encodeURIComponent(l.card_id)}`} className="font-mono text-xs text-primary hover:underline">
                          {l.card_id}
                        </Link>
                      </TD>
                      <TD><StateBadge grader={l.grader} grade={l.grade} condition={l.condition} /></TD>
                      <TD className="font-mono text-xs text-muted-fg">{l.cert_number ?? "—"}</TD>
                      <TD className="text-right font-mono">{Number(l.price_usdc).toFixed(2)}</TD>
                      <TD><StatusBadge status={l.status} /></TD>
                      <TD className="text-xs text-muted-fg">{fmtDate(l.created_at)}</TD>
                      <TD className="text-right">
                        {l.status === "active" && l.id ? (
                          <Button variant="ghost" size="sm" onClick={() => onCancelListing(l.id!)}>
                            <Trash2 className="h-3.5 w-3.5" />
                            {t("me.cancel") || "Cancel"}
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* WTB */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("me.wtb_offers") || "My buy offers"}</CardTitle>
            <CardDescription>{wtbs.length} {wtbs.length > 1 ? "offers" : "offer"}.</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH>Card</TH>
                  <TH>État</TH>
                  <TH className="text-right">Bid (USDC)</TH>
                  <TH>Status</TH>
                  <TH>Published</TH>
                  <TH className="text-right">Action</TH>
                </TR>
              </THead>
              <TBody>
                {loading && wtbs.length === 0 ? (
                  <tr><td colSpan={6}><div className="grid place-items-center py-6 text-muted-fg"><Loader2 className="h-4 w-4 animate-spin" /></div></td></tr>
                ) : wtbs.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <Empty title={t("me.no_wtb") || "No buy offers"} description={t("me.no_wtb.desc") || "Open a card detail page and post a WTB."} />
                    </td>
                  </tr>
                ) : (
                  wtbs.map((w) => (
                    <TR key={w.id}>
                      <TD>
                        <Link to={`/card/${encodeURIComponent(w.card_id)}`} className="font-mono text-xs text-primary hover:underline">
                          {w.card_id}
                        </Link>
                      </TD>
                      <TD><StateBadge grader={w.grader} grade={w.grade} condition={w.condition} /></TD>
                      <TD className="text-right font-mono">{Number(w.price_usdc).toFixed(2)}</TD>
                      <TD><StatusBadge status={w.status} /></TD>
                      <TD className="text-xs text-muted-fg">{fmtDate(w.created_at)}</TD>
                      <TD className="text-right">
                        {w.status === "active" && w.id ? (
                          <Button variant="ghost" size="sm" onClick={() => onCancelWtb(w.id!)}>
                            <Trash2 className="h-3.5 w-3.5" />
                            {t("me.cancel") || "Cancel"}
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Favorites */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("me.favorites") || "My favorites"}</CardTitle>
            <CardDescription>{favs.length} {favs.length > 1 ? "cards" : "card"} starred.</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH>Card</TH>
                  <TH>Name</TH>
                  <TH>Added</TH>
                  <TH className="text-right">Action</TH>
                </TR>
              </THead>
              <TBody>
                {loading && favs.length === 0 ? (
                  <tr><td colSpan={4}><div className="grid place-items-center py-6 text-muted-fg"><Loader2 className="h-4 w-4 animate-spin" /></div></td></tr>
                ) : favs.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <Empty
                        icon={<Star className="h-5 w-5" />}
                        title={t("me.no_favorites") || "No favorites yet"}
                        description={t("me.no_favorites.desc") || "Star a card from a card detail page."}
                      />
                    </td>
                  </tr>
                ) : (
                  favs.map((f) => (
                    <TR key={f.card_id}>
                      <TD>
                        <Link to={`/card/${encodeURIComponent(f.card_id)}`} className="font-mono text-xs text-primary hover:underline">
                          {f.card_id}
                        </Link>
                      </TD>
                      <TD>{f.card_name ?? "—"}</TD>
                      <TD className="text-xs text-muted-fg">{fmtDate(f.created_at)}</TD>
                      <TD className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => onRemoveFav(f.card_id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("me.remove") || "Remove"}
                        </Button>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, "success" | "warn" | "danger" | "neutral"> = {
    active: "success",
    cancelled: "neutral",
    canceled: "neutral",
    completed: "info" as never,
    closed: "neutral",
    expired: "warn",
    refunded: "danger",
  };
  const tone = (map[status] || "neutral") as "success" | "warn" | "danger" | "neutral";
  return (
    <Badge tone={tone} variant="soft">
      {status}
    </Badge>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-fg">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
